/**
 * Rich descriptions of how AcreMitra delivers each service.
 * Keyed by the numeric service id used in the DB seed (1..6).
 * Rendered inside the inquiry dialog above the form so buyers see exactly
 * what they're signing up for before submitting.
 */

export const SERVICE_DETAILS = {
  '1': {
    tagline: 'From shortlist to sale deed — we shoulder the heavy lifting.',
    steps: [
      { title: 'Investor discovery call', desc: 'A 30-min consultation with a senior land advisor to map your budget, holding horizon, and preferred zones.' },
      { title: 'Curated plot shortlist', desc: 'You receive 5–8 hand-picked plots that match your brief — each with clear title, RERA/mouza data, and comparable-sale benchmarks.' },
      { title: 'On-ground site visit', desc: 'We escort you (or your family) for a physical walkthrough, boundary check, neighbour-verification, and access-road assessment.' },
      { title: 'Negotiation & booking', desc: 'Our team negotiates the seller price on your behalf and locks the plot with a token deposit — full paperwork trail.' },
    ],
    timeline: '5–14 days from first call to token payment',
    owner: 'Assigned Land Advisor + Regional Manager',
  },
  '2': {
    tagline: 'Sell your plot at the best market rate — with zero listing fatigue.',
    steps: [
      { title: 'Free market valuation', desc: 'A ground-truth price estimate using recent comparable sales, circle rate, and locality growth data.' },
      { title: 'Professional listing', desc: 'Drone photography, floor-plan render (if any), and a premium listing across AcreMitra.com + partner portals.' },
      { title: 'Buyer pipeline', desc: 'Direct access to our verified investor network — HNI clients, NRIs, and end-users actively hunting in your micro-market.' },
      { title: 'Deal-closure support', desc: 'We handle site visits, negotiation, agreement drafting, and registration — you only sign.' },
    ],
    timeline: '15–90 days to closure, depending on plot & price',
    owner: 'Resale Desk (dedicated relationship manager)',
  },
  '3': {
    tagline: 'Every property document — verified, drafted, or registered.',
    steps: [
      { title: 'Title & ownership check', desc: '30-year title search, encumbrance certificate, mother-deed verification by our panel advocate.' },
      { title: 'Sale agreement drafting', desc: 'Bespoke sale agreement covering payment schedule, penalty clauses, and possession terms.' },
      { title: 'Registration & stamp duty', desc: 'We handle stamp duty calculation, sub-registrar booking, and physical registration — you sign in one visit.' },
      { title: 'Mutation & khatian update', desc: 'Post-registration mutation, khatian correction, and BL(&)LRO record updates.' },
    ],
    timeline: '7–30 days depending on document depth',
    owner: 'In-house legal team + panel advocates',
  },
  '4': {
    tagline: 'A trained escort, a checklist, and a written site-visit report.',
    steps: [
      { title: 'Pre-visit briefing', desc: 'You get plot dimensions, boundary map, access-road photos, and neighbour survey via WhatsApp before you arrive.' },
      { title: 'On-ground escort', desc: 'A local site executive meets you at the nearest landmark and drives you to the plot — no getting lost.' },
      { title: 'Physical inspection', desc: 'Boundary walk, drainage check, ambient noise, mobile signal, and photo-log of surrounding infra.' },
      { title: 'Written visit report', desc: 'Within 24 hrs you receive a PDF report with photos, geotag, pros/cons, and a red-flag list (if any).' },
    ],
    timeline: 'Booked within 48 hrs of your request',
    owner: 'Site Visit Coordinator (region-wise)',
  },
  '5': {
    tagline: 'Data-backed advice on where — and when — to invest.',
    steps: [
      { title: 'Portfolio review', desc: 'We map your existing real-estate holdings and identify diversification gaps by geography and asset class.' },
      { title: 'Micro-market ROI report', desc: 'Deterministic 5- and 10-year projected returns for your target locality, benchmarked against metro, expressway, and airport proximity.' },
      { title: 'Entry & exit timing', desc: 'Guidance on when to enter (pre-launch vs post-infra) and when to book profit (typical 5–8 year sweet-spot).' },
      { title: 'Tax & compliance advisory', desc: 'Capital-gains planning, 54F/54EC bonds, and NRI-repatriation guidance via our CA panel.' },
    ],
    timeline: '3–5 business days for a full advisory report',
    owner: 'Investment Advisor + external CA panel',
  },
  '6': {
    tagline: 'Your relationship with AcreMitra does not end at registration.',
    steps: [
      { title: 'Boundary demarcation', desc: 'We coordinate with the local surveyor to mark stone pillars and install a signboard, so your plot is unmistakable.' },
      { title: 'Fencing & land-fill quotes', desc: 'Pre-negotiated rates from our vetted contractors for fencing, levelling, and murum-filling.' },
      { title: 'Construction referral', desc: 'Direct handoff to our Architect service — cost estimate, blueprints, and vetted builder introductions.' },
      { title: 'Annual title monitoring', desc: 'One year of free encumbrance-certificate monitoring so no fraudulent lien can be filed on your plot.' },
    ],
    timeline: 'Ongoing — 12 months of complimentary support post-sale',
    owner: 'Post-Sale Care Desk',
  },
};

export const getServiceDetails = (id) => SERVICE_DETAILS[String(id)] || null;
